import React from 'react';
import { CalendarCheck, User as UserIcon, Building2, ArrowRight } from 'lucide-react';
import { SystemConfig } from '@/types';

interface LandingPageProps {
  systemConfig?: SystemConfig;
  onNavigateBooking: () => void;
  onLoginClick: (context: 'staff' | 'company' | 'customer') => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  systemConfig,
  onNavigateBooking,
  onLoginClick
}) => {
  return (
    <div className="max-w-5xl mx-auto px-4 py-16 animate-fade-in">
      <div className="text-center mb-10">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
          Welcome to {systemConfig?.general?.appName || 'K2 Occupational Health'}
        </h1>
        <p className="text-slate-500 text-sm max-w-lg mx-auto">
          Leading UK provider of occupational health compliance. Manage assessments, company
          records, and clinical certificates securely.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
        {/* Card 1: New Booking */}
        <div
          onClick={onNavigateBooking}
          className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-teal-500 hover:shadow-lg transition-all cursor-pointer group text-center flex flex-col items-center"
        >
          <div className="bg-teal-50 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-100 transition-colors">
            <CalendarCheck className="w-7 h-7 text-teal-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-1.5">Book a Medical</h2>
          <p className="text-slate-500 text-xs leading-relaxed">
            Schedule new appointments for individuals.
          </p>
          <button className="mt-5 text-teal-600 text-xs font-bold flex items-center gap-1 group-hover:gap-2 transition-all uppercase tracking-wider">
            Start Booking <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Card 2: Patient Portal */}
        <div
          onClick={() => onLoginClick('customer')}
          className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-blue-500 hover:shadow-lg transition-all cursor-pointer group text-center flex flex-col items-center"
        >
          <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
            <UserIcon className="w-7 h-7 text-blue-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-1.5">Patient Portal</h2>
          <p className="text-slate-500 text-xs leading-relaxed">
            View your visit history, clinical forms, and download certificates.
          </p>
          <button className="mt-5 text-blue-600 text-xs font-bold flex items-center gap-1 group-hover:gap-2 transition-all uppercase tracking-wider">
            Access Records <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Card 3: Company Login */}
        <div
          onClick={() => onLoginClick('company')}
          className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-purple-500 hover:shadow-lg transition-all cursor-pointer group text-center flex flex-col items-center"
        >
          <div className="bg-purple-50 w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:bg-purple-100 transition-colors">
            <Building2 className="w-7 h-7 text-purple-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-1.5">Corporate Portal</h2>
          <p className="text-slate-500 text-xs leading-relaxed">
            Manage workforce health assessments and organization certificates.
          </p>
          <button className="mt-5 text-purple-600 text-xs font-bold flex items-center gap-1 group-hover:gap-2 transition-all uppercase tracking-wider">
            Client Login <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
